import Analytics from '@components/charts/analytics/Analytics'
import VenueOpening from '@components/charts/venue/VenueOpening'
import { useAnalytics } from '@contexts/AnalyticsContext'
import {
  FetchVenueAnalyticsGraphParams,
  VenueAnalyticsGraphImpressionData,
} from '@models/venue/VenueAnalyticsGraph'
import { fetchAnalyticsGraph } from '@services/AnalyticsGraph'
import { useAppSelector } from '@store/hooks'
import { selectSelectedVenue } from '@store/VenueReducer'
import { PageTitle } from '@supplement-bacon/react-toolkit/components'
import { Col, Row, Space, Spin } from 'antd'
import { Dayjs } from 'dayjs'
import { useEffect, useState } from 'react'

interface Props {}

const ImpressionSection = ({}: Props) => {
  const venue = useAppSelector(selectSelectedVenue())
  const { selectedDates } = useAnalytics()

  const [chartData, setChartData] =
    useState<VenueAnalyticsGraphImpressionData>()
  const [openingSum, setOpeningSum] = useState<number>(0)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const fetchData = async () => {
    if (!venue) return

    setIsLoading(true)
    setError(undefined)

    if (!selectedDates) {
      setChartData(undefined)
      setError('No data available.')
      setIsLoading(false)
      return
    }

    const fetchParams: FetchVenueAnalyticsGraphParams = {
      entity: 'venue',
      entityId: venue.id,
      kind: 'impression-openings',
      startDate: selectedDates[0],
      endDate: selectedDates[1],
    }

    try {
      const response = await fetchAnalyticsGraph(fetchParams)
      const data = response.data as VenueAnalyticsGraphImpressionData
      setChartData(data)
      setOpeningSum(
        data.openings.reduce((sum, item) => sum + (item.count || 0), 0)
      )

      if (!data) {
        setError('No data available.')
      }
    } catch (error) {
      console.error('Error fetching data:', error)
      setError('An error occurred while fetching data.')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [venue, selectedDates])

  if (!venue) {
    return <Spin />
  }

  return (
    <Space orientation="vertical" className="w-full" size={16}>
      <PageTitle
        level={5}
        title={'Mobile application impression'}
        style={{ margin: 0 }}
        toolbar={
          <Analytics.Date
            rangePresets={Analytics.rangesPresets.venue}
            defaultValue={
              Analytics.rangesPresets.venue![0].value as [Dayjs, Dayjs]
            }
          />
        }
      />
      <Row gutter={[16, 16]}>
        <Col span={8}>
          <Analytics.Statistic
            isLoading={isLoading}
            title={'Total venue page opening'}
            value={openingSum}
          />
        </Col>
        <Col span={16}>
          <Analytics.RowStatistics
            entity="venue"
            entityId={venue.id}
            kind="venue-app-impression"
          />
        </Col>
      </Row>
      <VenueOpening chartData={chartData} isLoading={isLoading} error={error} />
    </Space>
  )
}

export default ImpressionSection
